/**
 * Objectives Item Drawer Component
 * 
 * Complete drawer solution for adding/editing objectives including:
 * - Rich Text Editor for Objectives field (no hyperlink support)
 * - Plain Text Area for Risks field
 * - Objective Risk Ranking selector
 * - Financial Statement Assertions multi-select
 * - Risk Mitigating Controls (toggle between link to existing controls or manual text)
 * - Form validation (Objectives and Risks are required)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Form, Input, Select, Spin } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import styled from 'styled-components';
import {
  RichTextEditorComponent,
  Toolbar,
  HtmlEditor,
  QuickToolbar,
  Inject
} from '@syncfusion/ej2-react-richtexteditor';
import { RootState, AppDispatch } from '../../../../store';
import { setAssertions } from '../../../../store/slices/riskAssessmentSlice';
import { setControlActivities } from '../../../../store/slices/controlActivitiesSlice';
import {
  getAssertions,
  parseAssertions,
} from '../../../../services/riskAssessmentService';
import {
  getControlActivities,
} from '../../../../services/controlActivitiesService';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import CloseCircleIcon from '../../../../assets/icons/ic-close-circle.svg?react';
import RiskMatrixModal from './RiskMatrixModal';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import { RiskRanking } from '../../../../services/types';
const { TextArea } = Input;

// ========== TYPE DEFINITIONS ==========

export interface ObjectivesFormData {
  id?: string; // Optional id for edit mode
  objectiveNumber?: string; // Display number for edit title (e.g. "01")
  objectives: string; // HTML string from RTE
  risks: string;
  controlRiskRanking: RiskRanking;
  riskRatingSeverityIndex?: number;
  riskRatingLikelihoodIndex?: number;
  financialStatementAssertionIds: string[]; // Assertion IDs for API
  controlActivityIds: string[]; // Control Activity IDs for linking
  controlText: string; // Free text for manual control input
  controlInputMode: 'link' | 'manual'; // Toggle between link to existing controls or manual text
}

export interface ObjectivesItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: Partial<ObjectivesFormData>;
  onClose: () => void;
  onSubmit: (values: ObjectivesFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  min-height: 120px !important;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    min-height: 44px !important;
    border-radius: 8px !important;
    align-items: center;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
    padding: 4px 11px !important;
  }

  .ant-select-selection-item {
    line-height: 32px !important;
  }
`;

// Custom Tag Component for Select
const SelectTag = styled.span<{ 
  $backgroundColor: string; 
  $color: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
  color: ${props => props.$color};
  margin: 2px;
`;

const StyledCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const RichTextEditorWrapper = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #D1D5DB;
  position: relative;
  overflow: visible;
  transition: border-color 0.3s;
  
  &:focus-within {
    border-color: #2563EB;
  }
  
  .e-richtexteditor {
    border: none;
    border-radius: 8px;
    font-family: Inter;
  }
  
  .e-richtexteditor .e-toolbar-wrapper {
    border-bottom: none;
  }
  
  .e-richtexteditor .e-rte-toolbar {
    background: white !important;
    border-bottom: 1px solid #E5E7EB;
    border-radius: 8px 8px 0 0;
    padding: 4px 8px;
    min-height: 44px;
  }
  
  .e-richtexteditor .e-toolbar {
    background: white !important;
    flex-wrap: wrap;
    overflow-x: auto;
    overflow-y: visible;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-items {
    flex-wrap: wrap;
    background: white !important;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-item {
    background: transparent !important;
  }
  
  .e-richtexteditor .e-rte-content {
    min-height: 150px;
    font-size: 14px;
    line-height: 20px;
    color: #1F2937;
    border-top: none;
  }
  
  .e-richtexteditor .e-rte-content .e-content {
    padding: 12px;
  }
  
  .e-toolbar-item {
    margin: 2px;
  }
  
  .e-toolbar-item .e-tbar-btn {
    color: #374151;
    border: none;
    background: transparent;
    padding: 6px 8px;
    min-width: 32px;
    height: 32px;
    border-radius: 4px;
  }
  
  .e-toolbar-item .e-tbar-btn:hover {
    background: #F3F4F6;
    color: #1F2937;
  }
  
  .e-toolbar-item .e-tbar-btn.e-active {
    background: #EFF6FF;
    color: #2563EB;
  }
  
  .e-toolbar .e-separator {
    border-color: #E5E7EB;
    margin: 0 4px;
  }
  
  .e-toolbar-item .e-dropdown-btn {
    color: #374151;
    border: none;
    background: transparent !important;
    padding: 6px 8px;
    height: 32px;
    border-radius: 4px;
  }
  
  .e-toolbar-item .e-dropdown-btn:hover {
    background: #F3F4F6 !important;
  }
  
  .e-richtexteditor .e-toolbar .e-tbar-btn-text,
  .e-richtexteditor .e-toolbar .e-btn-icon,
  .e-richtexteditor .e-toolbar .e-icons,
  .e-richtexteditor .e-toolbar-item .e-rte-dropdown-btn-text {
    background: transparent !important;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-extended {
    display: none;
  }
  
  .e-richtexteditor .e-rte-srctextarea,
  .e-rte-source-code-textarea,
  .e-source-content {
    display: none !important;
  }
  
  .e-rte-inline-popup {
    display: none !important;
  }
  
  .e-richtexteditor .e-rte-resize {
    display: none !important;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6B7280;
  font-size: 14px;
`;

const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const OrDivider = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 12px;
  color: #6B7280;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 40px;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid ${props => props.$active ? '#2563EB' : '#D1D5DB'};
  background: ${props => props.$active ? '#EFF6FF' : 'white'};
  color: ${props => props.$active ? '#2563EB' : '#6B7280'};

  &:hover {
    border-color: #2563EB;
    color: #2563EB;
  }
`;

const RiskChipContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 50px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 2px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 2px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 2px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 2px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const RiskChipHelper = styled.span`
  font-family: Inter;
  font-size: 13px;
  color: #9CA3AF;
  font-style: italic;
`;

// ========== MAIN COMPONENT ==========

const ObjectivesItemDrawer: React.FC<ObjectivesItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // Get assertions from Redux
  const assertionsFromRedux = useSelector((state: RootState) => state.riskAssessment.assertions);
  
  // Get control activities and selected report from Redux
  const { controlActivities, lastFetchedReportId } = useSelector(
    (state: RootState) => state.controlActivities
  );
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);

  // Form states
  const [form] = Form.useForm();
  const [objectives, setObjectivesState] = useState<string | undefined>(undefined);
  const [risks, setRisks] = useState('');
  const [controlRiskRanking, setControlRiskRanking] = useState<RiskRanking>('Unranked');
  const [financialStatementAssertionIds, setFinancialStatementAssertionIds] = useState<string[]>([]);
  const [controlActivityIds, setControlActivityIds] = useState<string[]>([]);
  const [controlText, setControlText] = useState('');
  const [controlInputMode, setControlInputMode] = useState<'link' | 'manual'>('link');
  const [riskRatingSeverityIndex, setRiskRatingSeverityIndex] = useState<number | null>(null);
  const [riskRatingLikelihoodIndex, setRiskRatingLikelihoodIndex] = useState<number | null>(null);

  // UI states
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);
  const [isLoadingAssertions, setIsLoadingAssertions] = useState(false);
  const [isLoadingControlActivities, setIsLoadingControlActivities] = useState(false);

  // Fetch assertions if not in Redux (offline first pattern)
  useEffect(() => {
    const fetchAssertionsIfNeeded = async () => {
      if (!open) return;
      
      // If assertions are already in Redux, skip fetching
      if (assertionsFromRedux.length > 0) {
        // console.log('✅ Assertions already in Redux, skipping fetch');
        return;
      }

      // Fetch assertions from API
      setIsLoadingAssertions(true);
      try {
        // console.log('🔄 Fetching assertions for drawer picker...');
        const response = await getAssertions();

        const parsedAssertions = parseAssertions(response.data || []);
        dispatch(setAssertions(parsedAssertions));
        // console.log(`✅ Assertions loaded for picker: ${parsedAssertions.length} items`);
      } catch (error) {
        console.error('❌ Failed to fetch assertions for picker:', error);
      } finally {
        setIsLoadingAssertions(false);
      }
    };

    fetchAssertionsIfNeeded();
  }, [open, assertionsFromRedux.length, dispatch]);

  // Fetch control activities if not in Redux (offline first pattern)
  useEffect(() => {
    const fetchControlActivitiesIfNeeded = async () => {
      if (!open) return;
      if (!selectedReport?.annualReportId) return;
      
      // If control activities are already loaded for this report, skip fetching
      if (controlActivities.length > 0 && lastFetchedReportId === selectedReport.annualReportId) {
        // console.log('✅ Control activities already in Redux, skipping fetch');
        return;
      }

      // Fetch control activities from API
      setIsLoadingControlActivities(true);
      try {
        // console.log('🔄 Fetching control activities for drawer picker...');
        const response = await getControlActivities({
          annualReportId: selectedReport.annualReportId,
          sorted: [{name: "ControlCode", direction: "ascending"}],
          skip: 0,
          take: 1000,
        });

        dispatch(setControlActivities({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId,
        }));
        // console.log(`✅ Control activities loaded for picker: ${response.data.length} items`);
      } catch (error) {
        console.error('❌ Failed to fetch control activities for picker:', error);
      } finally {
        setIsLoadingControlActivities(false);
      }
    };

    fetchControlActivitiesIfNeeded();
  }, [open, selectedReport?.annualReportId, controlActivities.length, lastFetchedReportId, dispatch]);

  // Reset form when initialValues change
  useEffect(() => {
    if (mode === 'edit' && initialValues) {
      // Edit mode - load existing values
      setObjectivesState(initialValues.objectives || '');
      setRisks(initialValues.risks || '');
      setControlRiskRanking(initialValues.controlRiskRanking || 'Unranked' as RiskRanking);
      setRiskRatingSeverityIndex(initialValues.riskRatingSeverityIndex ?? null);
      setRiskRatingLikelihoodIndex(initialValues.riskRatingLikelihoodIndex ?? null);
      setFinancialStatementAssertionIds(initialValues.financialStatementAssertionIds || []);
      setControlActivityIds(initialValues.controlActivityIds || []);
      setControlText(initialValues.controlText || '');
      setControlInputMode(initialValues.controlInputMode || 'link');
      
      form.setFieldsValue({
        objectives: initialValues.objectives,
        risks: initialValues.risks,
        controlRiskRanking: initialValues.controlRiskRanking,
        financialStatementAssertionIds: initialValues.financialStatementAssertionIds,
        controlActivityIds: initialValues.controlActivityIds,
        controlText: initialValues.controlText,
      });
    } else {
      // Add mode - reset to empty with defaults
      resetForm();
    }
  }, [open, mode, initialValues]);

  // Watch for field changes and validate
  useEffect(() => {
    const checkFormValidation = () => {
      // Strip HTML tags to check if objectives has actual text content
      const objectivesText = objectives?.replace(/<[^>]*>/g, '').trim() || '';
      const hasObjectives = objectivesText !== '';
      const hasRisks = !!risks && risks.trim() !== '';
      let hasControlActivities = false;
      if (controlInputMode === 'link') {
        hasControlActivities = controlActivityIds.length > 0;
      } else {
        hasControlActivities = !!controlText && controlText.trim() !== '';
      }
      const isValid = hasObjectives && hasRisks && hasControlActivities;
      setIsFormValid(isValid);
    };

    checkFormValidation();
  }, [objectives, risks, controlInputMode, controlActivityIds, controlText]);

  const resetForm = () => {
    setObjectivesState('');
    setRisks('');
    setControlRiskRanking('Unranked' as RiskRanking);
    setRiskRatingSeverityIndex(null);
    setRiskRatingLikelihoodIndex(null);
    setFinancialStatementAssertionIds([]);
    setControlActivityIds([]);
    setControlText('');
    setControlInputMode('link');
    form.resetFields();
  };

  const handleClose = () => {
    setIsFormValid(false);
    resetForm();
    onClose();
  };

  const handleObjectivesChange = (args: any) => {
    setObjectivesState(args.value);
  };

  // Ref for RTE
  const rteRef = useRef<any>(null);

  // Enable spellcheck after RTE is created
  const handleRTECreated = useCallback(() => {
    if (rteRef.current) {
      try {
        // For div mode (contenteditable)
        const editableElement = rteRef.current.contentModule?.getEditPanel?.();
        if (editableElement) {
          editableElement.setAttribute('spellcheck', 'true');
        }
        // For iframe mode
        const iframeDoc = rteRef.current.contentModule?.getDocument?.();
        if (iframeDoc?.body) {
          iframeDoc.body.setAttribute('spellcheck', 'true');
        }
      } catch (error) {
        console.warn('Could not enable spellcheck on RTE', error);
      }
    }
  }, []);

  const handleRiskMatrixSelect = (
    riskLevel: RiskRanking,
    severityIndex: number,
    likelihoodIndex: number
  ) => {
    setControlRiskRanking(riskLevel);
    setRiskRatingSeverityIndex(severityIndex);
    setRiskRatingLikelihoodIndex(likelihoodIndex);
    form.setFieldValue('controlRiskRanking', riskLevel);
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      const formData: ObjectivesFormData = {
        objectives: objectives || '',
        risks,
        controlRiskRanking,
        riskRatingSeverityIndex: riskRatingSeverityIndex ?? undefined,
        riskRatingLikelihoodIndex: riskRatingLikelihoodIndex ?? undefined,
        financialStatementAssertionIds,
        controlActivityIds: controlInputMode === 'link' ? controlActivityIds : [],
        controlText: controlInputMode === 'manual' ? controlText : '',
        controlInputMode,
      };

      // Include id for edit mode
      if (mode === 'edit' && initialValues) {
        formData.id = initialValues.id;
      }

      // console.log('formData', formData);

      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Custom tag render for Financial Statement Assertions
  const renderAssertionTag = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <SelectTag
        $backgroundColor="#FEF9C3"
        $color="#854D0E"
        onMouseDown={onPreventMouseDown}
      >
        {label}
        {closable && (
          <StyledCloseIcon
            $circleFill="#FDE68A"
            $strokeColor="#854D0E"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
        )}
      </SelectTag>
    );
  };

  // Custom tag render for Control Activities
  const renderControlActivityTag = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <SelectTag
        $backgroundColor="#EFF6FF"
        $color="#1E40AF"
        onMouseDown={onPreventMouseDown}
      >
        {label}
        {closable && (
          <StyledCloseIcon
            $circleFill="#DBEAFE"
            $strokeColor="#1E40AF"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
        )}
      </SelectTag>
    );
  };

  const drawerTitle = mode === 'add'
    ? 'Add Objective'
    : `Edit Objective${initialValues?.objectiveNumber ? ` - ${initialValues.objectiveNumber}` : ''}`;

  return (
    <RightContentDrawer
      open={open}
      title={drawerTitle}
      width={700}
      onClose={handleClose}
      footer={
        <ButtonGroup>
          <Button
            type="default"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleFormSubmit}
            loading={loading}
            disabled={!isFormValid || !isAdmin}
          >
            {loading ? 'Saving...' : mode === 'add' ? 'Add Objective' : 'Save Changes'}
          </Button>
        </ButtonGroup>
      }
    >
      <StyledForm
        id="objectives-form"
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {/* Objectives (Rich Text Editor) */}
        <Form.Item
          name="objectives"
          rules={[
            { required: true, message: 'Please enter objective' },
          ]}
        >
          <div>
            <FormLabel>Objective</FormLabel>
            <RichTextEditorWrapper>
              <RichTextEditorComponent
                ref={rteRef}
                value={objectives}
                change={handleObjectivesChange}
                created={handleRTECreated}
                toolbarSettings={{
                  enable: true,
                  enableFloating: false,
                  items: [
                    'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
                    'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
                    'Formats', 'Alignments', '|',
                    'BulletFormatList', 'NumberFormatList', '|',
                    'Undo', 'Redo'
                  ]
                }}
                quickToolbarSettings={{
                  enable: false
                }}
                format={{
                  width: '100px'
                }}
              >
                <Inject services={[Toolbar, HtmlEditor, QuickToolbar]} />
              </RichTextEditorComponent>
            </RichTextEditorWrapper>
          </div>
        </Form.Item>

        {/* Risks (Plain Text Area) */}
        <Form.Item
          name="risks"
          rules={[
            { required: true, message: 'Please enter risk' },
            { whitespace: true, message: 'Risk cannot be empty' },
          ]}
        >
          <div>
            <FormLabel>Risk</FormLabel>
            <StyledTextArea
              value={risks}
              onChange={(e) => setRisks(e.target.value)}
              placeholder="Enter risk associated with this objective"
              rows={5}
              spellCheck={true}
            />
          </div>
        </Form.Item>

        {/* Control Risk Ranking */}
        <Form.Item name="controlRiskRanking">
          <div>
            <FormLabel>Objective Risk Ranking</FormLabel>
            <RiskChipContainer>
              <RiskChip 
                $risk={controlRiskRanking} 
                onClick={() => setIsRiskMatrixModalOpen(true)}
              >
                {controlRiskRanking}
              </RiskChip>
              <RiskChipHelper>Click to open Risk Matrix</RiskChipHelper>
            </RiskChipContainer>
            <InfoText>
              Defaults to "Unranked". Click the chip to use the Risk Matrix for selecting ranking.
            </InfoText>
          </div>
        </Form.Item>

        {/* Financial Statement Assertions */}
        <Form.Item name="financialStatementAssertionIds">
          <div>
            <FormLabel>Financial Statement Assertions</FormLabel>
            {isLoadingAssertions ? (
              <LoadingWrapper>
                <Spin size="small" />
                <span>Loading assertions...</span>
              </LoadingWrapper>
            ) : (
              <>
                <StyledSelect
                  mode="multiple"
                  value={financialStatementAssertionIds}
                  onChange={(value) => setFinancialStatementAssertionIds(value as string[])}
                  placeholder="Select financial statement assertions"
                  tagRender={renderAssertionTag}
                  options={assertionsFromRedux.map(assertion => ({
                    value: assertion.id,
                    label: assertion.name,
                  }))}
                  notFoundContent={assertionsFromRedux.length === 0 ? 'No assertions available' : 'Not found'}
                />
                <InfoText>
                  Select assertions from the available list
                </InfoText>
              </>
            )}
          </div>
        </Form.Item>

        {/* Risk Mitigating Controls */}
        <Form.Item name="controlText">
          <div>
            <FormLabel>Risk Mitigating Controls</FormLabel>
            <InfoText style={{ marginBottom: '10px' }}>
              {controlInputMode === 'link'
                ? 'To show risk mitigation, please link existing controls from the library or select \'No controls exist\' to provide a written justification. You must select at least one control or enter a rationale to proceed.'
                : 'Provide a written justification explaining why no control activities exist for this objective'}
            </InfoText>
            <ToggleGroup>
              <ToggleButton
                type="button"
                $active={controlInputMode === 'link'}
                onClick={() => setControlInputMode('link')}
              >
                Link to existing controls
              </ToggleButton>
              <OrDivider>OR</OrDivider>
              <ToggleButton
                type="button"
                $active={controlInputMode === 'manual'}
                onClick={() => setControlInputMode('manual')}
              >
                No controls exist
              </ToggleButton>
            </ToggleGroup>
            
            {controlInputMode === 'link' ? (
              <div style={{ marginTop: '-4px' }}>
                {isLoadingControlActivities ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Spin size="small" />
                  </div>
                ) : (
                  <StyledSelect
                    mode="multiple"
                    value={controlActivityIds}
                    onChange={(values) => setControlActivityIds(values as string[])}
                    placeholder="Select control activities"
                    tagRender={renderControlActivityTag}
                    showSearch
                    filterOption={(input, option) => {
                      const control = controlActivities.find(c => c.id === option?.value);
                      if (!control) return false;
                      const searchText = `${control.controlCode} ${control.controlDescription?.what || ''}`.toLowerCase();
                      return searchText.includes(input.toLowerCase());
                    }}
                    options={controlActivities.map((control) => ({
                      value: control.id,
                      label: `${control.controlCode} - ${control.controlDescription?.what || ''}`,
                    }))}
                    notFoundContent={controlActivities.length === 0 ? 'No control activities available' : 'Not found'}
                  />
                )}
              </div>
            ) : (
              <div style={{ marginTop: '-4px' }}>
                <StyledTextArea
                  value={controlText}
                  onChange={(e) => setControlText(e.target.value)}
                  placeholder="Enter your rationale or justification for why no controls exist..."
                  rows={4}
                  spellCheck={true}
                />
              </div>
            )}
          </div>
        </Form.Item>
      </StyledForm>

      {/* Risk Matrix Modal */}
      <RiskMatrixModal
        open={isRiskMatrixModalOpen}
        onClose={() => setIsRiskMatrixModalOpen(false)}
        onSelect={handleRiskMatrixSelect}
        selectedSeverityIndex={riskRatingSeverityIndex}
        selectedLikelihoodIndex={riskRatingLikelihoodIndex}
      />
    </RightContentDrawer>
  );
};

export default ObjectivesItemDrawer;

